
import { useEffect, useRef, useCallback, useState } from "react";

interface UseActivePointProps {
    scrollAreaRef: React.RefObject<HTMLElement>;
    pointSelector: string;
    selectedFrame: string;
    offset?: number;
    // New API: returns array of active visible point metas
    onActiveChange?: (activePoints: PointMeta[]) => void;
}

type PointMeta = {
    id: string;
    left: number;
    right: number;
    center: number;
    width: number;
};

/**
 * Optimized hook to determine the active point inside a horizontal scroll area.
 * Behavior:
 * - Maintains a cache of point positions for fast lookups
 * - Uses IntersectionObserver to track which points are currently visible
 * - Throttles updates with requestAnimationFrame
 * - Selection logic: if any visible point contains the target offset => that point.
 *   Otherwise pick the closest point to the left of the target (previous), if none
 *   to the left pick the nearest right point.
 */
export const useActivePointOnScroll = ({
    scrollAreaRef,
    pointSelector,
    selectedFrame,
    offset = window.innerWidth / 2 - 50,
    onActiveChange,
}: UseActivePointProps) => {
    const pointsMapRef = useRef<Map<string, PointMeta>>(new Map());
    const visibleSetRef = useRef<Set<string>>(new Set());
    const rafScheduledRef = useRef<number | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const lastActiveId = useRef<string | null>(null);
    const [activePoints, setActivePoints] = useState<PointMeta[]>([]);

    const buildPoints = useCallback(() => {
        const container = scrollAreaRef.current;
        if (!container) return;
        const containerRect = container.getBoundingClientRect();
        const nodes = Array.from(container.querySelectorAll<HTMLElement>(pointSelector));
        const map = new Map<string, PointMeta>();

        for (const el of nodes) {
            const rect = el.getBoundingClientRect();
            // compute left relative to scroll content coordinate (scrollLeft origin)
            const left = Math.round(container.scrollLeft + (rect.left - containerRect.left));
            const width = Math.round(rect.width || el.offsetWidth || 0);
            const right = left + width;
            const center = left + Math.round(width / 2);
            if (el.id) map.set(el.id, { id: el.id, left, right, center, width });
        }

        pointsMapRef.current = map;
    }, [scrollAreaRef, pointSelector]);

    const updateActive = useCallback(() => {
        const container = scrollAreaRef.current;
        if (!container) return;

        const map = pointsMapRef.current;
        if (map.size === 0) return;

        // Build list of visible metas (those observed as intersecting)
        const visibles: PointMeta[] = [];
        visibleSetRef.current.forEach((id) => {
            const m = map.get(id);
            if (m) visibles.push(m);
        });

        // sort by left position
        visibles.sort((a, b) => a.left - b.left);

        // update state with visible points
        setActivePoints(visibles);

        // Call new API with full visible metas (only when changed)
        const idsKey = visibles.map((v) => v.id).join(",");
        if (lastActiveId.current !== idsKey) {
            lastActiveId.current = idsKey;
            onActiveChange?.(visibles);
        }
    }, [scrollAreaRef, onActiveChange]);

    const scheduleUpdate = useCallback(() => {
        if (rafScheduledRef.current !== null) return;
        rafScheduledRef.current = requestAnimationFrame(() => {
            rafScheduledRef.current = null;
            updateActive();
        });
    }, [updateActive]);

    useEffect(() => {
        const container = scrollAreaRef.current;
        if (!container) return;

        // build initial map
        buildPoints();

        // IntersectionObserver to track visibility
        const obs = new IntersectionObserver(
            (entries) => {
                let changed = false;
                for (const entry of entries) {
                    const el = entry.target as HTMLElement;
                    if (!el.id) continue;
                    const wasVisible = visibleSetRef.current.has(el.id);
                    const nowVisible = entry.isIntersecting && entry.intersectionRatio > 0.05;
                    if (nowVisible && !wasVisible) {
                        visibleSetRef.current.add(el.id);
                        changed = true;
                    } else if (!nowVisible && wasVisible) {
                        visibleSetRef.current.delete(el.id);
                        changed = true;
                    }
                }
                if (changed) scheduleUpdate();
            },
            { root: container, threshold: [0, 0.05, 0.1] }
        );

        observerRef.current = obs;

        const observedNodes = new Set<HTMLElement>();
        const observeCurrentPoints = () => {
            buildPoints();
            const currentNodes = Array.from(container.querySelectorAll<HTMLElement>(pointSelector));
            for (const node of currentNodes) {
                if (!observedNodes.has(node)) {
                    obs.observe(node);
                    observedNodes.add(node);
                }
            }
            scheduleUpdate();
        };

        observeCurrentPoints();

        const mutationObserver = typeof MutationObserver !== "undefined"
            ? new MutationObserver(() => observeCurrentPoints())
            : null;

        mutationObserver?.observe(container, {
            childList: true,
            subtree: true,
        });

        // listen to scroll/wheel/touch and schedule update
        const onScroll = () => scheduleUpdate();
        container.addEventListener("scroll", onScroll, { passive: true });
        container.addEventListener("wheel", onScroll, { passive: true });
        container.addEventListener("touchmove", onScroll, { passive: true });

        // Watch for layout/size changes to rebuild map positions
        if (typeof ResizeObserver !== "undefined") {
            const ro = new ResizeObserver(() => {
                buildPoints();
                scheduleUpdate();
            });
            resizeObserverRef.current = ro;
            resizeObserverRef.current.observe(container);
        }

        // initial calculation
        scheduleUpdate();

        return () => {
            if (observerRef.current) {
                try {
                    observedNodes.forEach((n) => {
                        if (observerRef.current) observerRef.current.unobserve(n);
                    });
                } catch (e) {
                    // noop
                }
                observerRef.current.disconnect();
                observerRef.current = null;
            }
            if (resizeObserverRef.current) {
                try {
                    resizeObserverRef.current.disconnect();
                } catch (e) {
                    // noop
                }
                resizeObserverRef.current = null;
            }
            mutationObserver?.disconnect();
            container.removeEventListener("scroll", onScroll);
            container.removeEventListener("wheel", onScroll);
            container.removeEventListener("touchmove", onScroll);
            if (rafScheduledRef.current) {
                cancelAnimationFrame(rafScheduledRef.current);
                rafScheduledRef.current = null;
            }
        };
    }, [scrollAreaRef, pointSelector, buildPoints, scheduleUpdate]);

    useEffect(() => {
        // 🔥 Force recalculation when timeframe/frame changes
        buildPoints();
        scheduleUpdate();
    }, [selectedFrame, buildPoints, scheduleUpdate]);


    // return array of active visible points
    return activePoints;
};
